import { useState, type ReactNode } from 'react';
import { LinkSlashIcon } from '@heroicons/react/24/outline';
import { useDmPatient } from '../../../../../hooks/useDmPatient';
import { useDmExcludedTeeth } from '../../../../../hooks/useDmExcludedTeeth';
import { DmControlMain } from './DmControlMain';
import type { DmDraft } from './dmControlDraft';
import { DmButton, Spinner } from './DmControlPrimitives';
import { DmModalShell, type DmShellLayout } from './DmModalShell';
import { errorText, monitoringState } from './dmControlUtils';
import { ExcludedTeethEditor } from './excluded-teeth/ExcludedTeethEditor';
import type { DmControlStep } from './useDmControlRoute';
import { useFlash } from './useFlash';

const TITLE = 'Dental Monitoring';

function LoadingModal({ layout, onClose }: { layout: DmShellLayout; onClose: () => void }) {
    return (
        <DmModalShell
            layout={layout}
            title={TITLE}
            subtitle={
                <span className="inline-block h-3.5 w-44 animate-pulse rounded bg-gray-200 align-middle" />
            }
            onClose={onClose}
            footer={
                <>
                    <span className="mr-auto inline-flex items-center gap-2 text-xs text-gray-500">
                        <Spinner className="h-4 w-4 text-[#007E87]" />
                        Loading from Dental Monitoring…
                    </span>
                    <DmButton kind="primary" disabled>
                        Save changes
                    </DmButton>
                </>
            }
        >
            <div className="animate-pulse" aria-busy="true">
                <div className="grid grid-cols-5 gap-x-6 border-b border-gray-200 py-4">
                    {[0, 1, 2, 3, 4].map((index) => (
                        <div key={index}>
                            <div className="h-3 w-14 rounded bg-gray-100" />
                            <div className="mt-2 h-4 w-28 rounded bg-gray-200" />
                        </div>
                    ))}
                </div>
                <div className="pt-5">
                    <div className="h-4 w-32 rounded bg-gray-200" />
                    <div className="mt-4 grid grid-cols-[300px_minmax(0,1fr)] gap-6">
                        <div className="h-20 rounded bg-gray-100" />
                        <div className="space-y-3">
                            {[0, 1, 2].map((index) => (
                                <div key={index} className="h-8 rounded bg-gray-100" />
                            ))}
                        </div>
                    </div>
                </div>
                <div className="mt-5 border-t border-gray-200 py-4">
                    <div className="h-3 w-24 rounded bg-gray-100" />
                    <div className="mt-4 h-2 w-full rounded bg-gray-100" />
                </div>
                <div className="border-t border-gray-200 py-5">
                    <div className="h-4 w-40 rounded bg-gray-200" />
                    <div className="mt-3 h-6 w-full rounded bg-gray-100" />
                    <div className="mt-2 h-6 w-full rounded bg-gray-100" />
                </div>
            </div>
        </DmModalShell>
    );
}

interface MessageModalProps {
    layout: DmShellLayout;
    patientName: string;
    heading: string;
    children: ReactNode;
    onClose: () => void;
    action?: ReactNode;
}

function MessageModal({
    layout,
    patientName,
    heading,
    children,
    onClose,
    action
}: MessageModalProps) {
    return (
        <DmModalShell
            layout={layout}
            title={TITLE}
            subtitle={patientName}
            onClose={onClose}
            footer={
                <>
                    {action}
                    {layout === 'modal' && <DmButton onClick={onClose}>Close</DmButton>}
                </>
            }
        >
            <div className="flex flex-col items-center py-16 text-center">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                    <LinkSlashIcon className="h-6 w-6" />
                </span>
                <h3 className="mt-4 text-sm font-semibold text-gray-800">{heading}</h3>
                <p className="mt-1 max-w-sm text-[13px] text-gray-500">{children}</p>
            </div>
        </DmModalShell>
    );
}

interface DmMonitoringControlProps {
    patientId: string;
    patientName: string;
    /** Which step shows; owned by the URL (see DmControlRoute). */
    step: DmControlStep;
    onOpenTeeth: () => void;
    onLeaveTeeth: () => void;
    onClose: () => void;
    layout?: DmShellLayout;
}

// Everything DM runs for one patient, saved together; excluded teeth is a second step.
export function DmMonitoringControl({
    patientId,
    patientName,
    step,
    onOpenTeeth,
    onLeaveTeeth,
    onClose,
    layout = 'modal'
}: DmMonitoringControlProps) {
    const { data: patient, isLoading, error, refetch } = useDmPatient(patientId);
    const excluded = useDmExcludedTeeth(patientId, !!patient);
    // Held here, not in the main view, so a trip to the teeth step keeps unsaved changes.
    const [edits, setEdits] = useState<DmDraft | null>(null);
    const [teethSaved, setTeethSaved] = useFlash<true>();

    if (isLoading) return <LoadingModal layout={layout} onClose={onClose} />;
    if (error) {
        return (
            <MessageModal
                layout={layout}
                patientName={patientName}
                heading="Couldn't reach Dental Monitoring"
                onClose={onClose}
                action={<DmButton onClick={() => void refetch()}>Retry</DmButton>}
            >
                {errorText(error)}
            </MessageModal>
        );
    }
    if (!patient) {
        return (
            <MessageModal
                layout={layout}
                patientName={patientName}
                heading="Not linked to Dental Monitoring"
                onClose={onClose}
            >
                {patientName} doesn&rsquo;t have a Dental Monitoring patient yet, so there&rsquo;s
                nothing to control here. Link the patient in Dental Monitoring, then reopen this
                window.
            </MessageModal>
        );
    }

    // A stopped monitoring is read-only, even when the URL asks for the editor.
    if (step === 'teeth' && excluded.data && monitoringState(patient.status) !== 'stopped') {
        return (
            <ExcludedTeethEditor
                layout={layout}
                patientId={patientId}
                patientName={patientName}
                saved={excluded.data}
                onCancel={onLeaveTeeth}
                onSaved={() => {
                    setTeethSaved(true);
                    onLeaveTeeth();
                }}
                onClose={onClose}
            />
        );
    }

    return (
        <DmControlMain
            layout={layout}
            patientId={patientId}
            patientName={patientName}
            patient={patient}
            excluded={excluded.data}
            excludedError={excluded.error ? errorText(excluded.error) : null}
            teethSaved={teethSaved === true}
            onEditTeeth={onOpenTeeth}
            onClose={onClose}
            edits={edits}
            setEdits={setEdits}
        />
    );
}
